// Guarding a page's load against its own older responses.
//
// THE BUG THIS EXISTS FOR, which five list pages had (found 2026-09-02).
//
// Every list page re-runs its `load()` on each filter change, search keystroke
// and view step. Nothing cancelled the previous one and nothing checked which
// answer was arriving, so two requests in flight resolved in whatever order
// the network gave them back — and a SLOW EARLIER request landing last would
// overwrite the correct newer result. Typing "exc" into the resource search
// could leave the list showing the results for "ex", and stepping the schedule
// forward twice quickly could leave last week's bookings on screen under this
// week's heading. Nothing looked broken; the data was simply wrong, and a
// second keystroke usually hid it again.
//
// Debouncing does not fix this — it makes it rarer, which is worse, because a
// wrong schedule that appears once a fortnight is a wrong schedule nobody
// believes the report of.
//
// The fix is a sequence number rather than an AbortController on purpose: most
// of these loads are several requests in a `Promise.all`, some of which are
// shared with other panels, and aborting them individually would mean deciding
// what a half-cancelled page shows. Ignoring a stale ANSWER is the smaller,
// more obviously correct rule — the request still completes, its result is
// simply not allowed to overwrite a newer one.
import { useRef } from 'react';

/**
 * Returns a `begin()` to call at the top of a load. `begin()` hands back an
 * `isCurrent()` — check it before every `setState` that follows an `await`,
 * including in `catch` and `finally`:
 *
 *     const begin = useLatestRequest();
 *     const load = useCallback(async () => {
 *       const isCurrent = begin();
 *       const result = await api.list();
 *       if (!isCurrent()) return;
 *       setRows(result.rows);
 *     }, [begin, filters]);
 *
 * `begin` is stable, so it is safe in a `useCallback` dependency list.
 */
export function useLatestRequest(): () => () => boolean {
  const latest = useRef(0);
  const begin = useRef(() => {
    const mine = (latest.current += 1);
    return () => mine === latest.current;
  });
  return begin.current;
}
